/**
 * The Security tab's password forms — change it, or set a first one.
 *
 * The Clerk work (and the step-up it needs) is `useAccountPassword` in
 * @tonnie/sdk; the rule is `changePasswordSchema` / `setPasswordSchema` in
 * @tonnie/schemas. What is left here is the screen's side: the form, the
 * words for a refusal (`useAuthErrorMessage` — never Clerk's English), a
 * report for anything unexpected, and the confirmation.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthErrorMessage, useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import {
  changePasswordSchema,
  setPasswordSchema,
  type ChangePasswordInput,
  type SetPasswordInput,
} from '@tonnie/schemas';
import { useAccountPassword, type PasswordOutcome } from '@tonnie/sdk';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

/** Turn an outcome into what the screen shows. `true` when the password was saved. */
function useSettle() {
  const { t } = useTranslation();
  const describeAuthError = useAuthErrorMessage();
  const { captureException } = useErrorCapture();
  const [error, setError] = useState<string | null>(null);

  const settle = useCallback(
    (result: PasswordOutcome, action: 'change' | 'set'): boolean => {
      switch (result.outcome) {
        case 'done':
          setError(null);
          toast.success(t('validation.passwordUpdated'));
          return true;
        case 'cancelled':
          // The person dismissed the step-up — nothing to tell them.
          return false;
        case 'failed': {
          const failure = describeAuthError(result.error, t('validation.passwordUpdateFailed'));
          if (!failure.expected) {
            captureException(
              result.error instanceof Error ? result.error : new Error(String(result.error)),
              { component: 'use-password-forms', action }
            );
          }
          setError(failure.message);
          return false;
        }
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled password outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [t, describeAuthError, captureException]
  );

  return { error, setError, settle };
}

export function useChangePasswordForm({ onSaved }: { onSaved: () => void }) {
  const { change } = useAccountPassword();
  const { error, setError, settle } = useSettle();
  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      signOutOfOtherSessions: true,
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    setError(null);
    const saved = settle(
      await change({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        signOutOfOtherSessions: data.signOutOfOtherSessions,
      }),
      'change'
    );
    if (saved) {
      form.reset();
      onSaved();
    }
  });

  return { form, onSubmit, error, isSubmitting: form.formState.isSubmitting };
}

export function useSetPasswordForm({ onSaved }: { onSaved: () => void }) {
  const { set } = useAccountPassword();
  const { error, setError, settle } = useSettle();
  const form = useForm<SetPasswordInput>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '', signOutOfOtherSessions: false },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    setError(null);
    const saved = settle(
      await set({
        newPassword: data.newPassword,
        signOutOfOtherSessions: data.signOutOfOtherSessions,
      }),
      'set'
    );
    if (saved) {
      form.reset();
      onSaved();
    }
  });

  return { form, onSubmit, error, isSubmitting: form.formState.isSubmitting };
}
